import * as THREE from "three";
import { getSceneActions, getSceneState } from "../stores/sceneStore";
import type { BodyUUID } from "../types/primitives";

type SceneBodyHierarchyEntry = { children?: unknown[] };

function computeBodyWorldMatrix(bodyObj: THREE.Object3D): THREE.Matrix4 {
  const chain: THREE.Object3D[] = [bodyObj];
  const seen = new Set<THREE.Object3D>([bodyObj]);
  let cur: THREE.Object3D | null = bodyObj.parent;
  while (cur) {
    if (seen.has(cur)) break;
    seen.add(cur);
    chain.push(cur);
    cur = cur.parent;
  }
  const world = new THREE.Matrix4().identity();
  for (let i = chain.length - 1; i >= 0; i--) {
    const n = chain[i];
    if (typeof n.updateMatrix === "function") n.updateMatrix();
    if (n.matrix) world.multiply(n.matrix);
  }
  return world;
}

function computeParentWorldMatrix(bodyObj: THREE.Object3D): THREE.Matrix4 {
  const chain: THREE.Object3D[] = [];
  const seen = new Set<THREE.Object3D>();
  let cur: THREE.Object3D | null = bodyObj.parent;
  while (cur) {
    if (seen.has(cur)) break;
    seen.add(cur);
    chain.push(cur);
    cur = cur.parent;
  }
  const world = new THREE.Matrix4().identity();
  for (let i = chain.length - 1; i >= 0; i--) {
    const n = chain[i];
    if (typeof n.updateMatrix === "function") n.updateMatrix();
    if (n.matrix) world.multiply(n.matrix);
  }
  return world;
}

/**
 * Builds a map: bodyUuid -> parentBodyUuid | null from the scene store's body data.
 * Preferred over scene-graph traversal — the store has canonical UUIDs without sanitization issues.
 */
export function buildParentMapFromSceneState(): Map<string, string | null> {
  const bodies = getSceneState().bodies || {};
  const map = new Map<string, string | null>();
  for (const [uuid, body] of Object.entries(bodies)) {
    if (!map.has(uuid)) map.set(uuid, null);
    const children = (body as SceneBodyHierarchyEntry).children ?? [];
    for (const childUuid of children) {
      if (typeof childUuid === "string") map.set(childUuid, uuid);
    }
  }
  return map;
}

/**
 * Returns only bodies whose ancestor is NOT also in the selection set.
 * This prevents double-transforming when a parent and child are both selected.
 */
export function filterTopmostBodies(
  bodyUuids: string[],
  parentMap: Map<string, string | null>
): string[] {
  const uuidSet = new Set(bodyUuids);
  return bodyUuids.filter((uuid) => {
    let cur: string | null | undefined = parentMap.get(uuid);
    while (cur != null) {
      if (uuidSet.has(cur)) return false;
      cur = parentMap.get(cur);
    }
    return true;
  });
}

export type MultiBodySnapshot = {
  bodyUuid: string;
  startLocalPos: [number, number, number];
  startQuat: [number, number, number, number];
  /** Body's world position at drag start, used for rotation-around-centroid math. */
  bodyWorldPos: THREE.Vector3;
  /** Parent body's world rotation quaternion, to convert world deltas to parent-local. */
  parentWorldQuat: THREE.Quaternion;
  /** Parent body's world origin (translation), used for position conversion in rotation mode. */
  parentWorldOrigin: THREE.Vector3;
};

/**
 * Captures a snapshot of transform state for each body UUID, suitable for use during a
 * multi-body drag. Reads current positions/quats from the scene store.
 */
export function captureMultiBodySnapshots(
  bodyUuids: string[],
  sceneObjects: Record<string, THREE.Object3D> | null | undefined
): MultiBodySnapshot[] {
  if (!sceneObjects) return [];
  const robotActions = getSceneActions();
  const snapshots: MultiBodySnapshot[] = [];

  for (const uuid of bodyUuids) {
    const sceneObj = sceneObjects[uuid];
    if (!sceneObj) continue;

    const parentWorldMat = computeParentWorldMatrix(sceneObj);
    const parentWorldQuat = new THREE.Quaternion().setFromRotationMatrix(parentWorldMat);
    const parentWorldOrigin = new THREE.Vector3().setFromMatrixPosition(parentWorldMat);

    const bodyWorldMat = computeBodyWorldMatrix(sceneObj);
    const bodyWorldPos = new THREE.Vector3().setFromMatrixPosition(bodyWorldMat);

    const bodyPos = (robotActions.getBodyPosition(uuid as BodyUUID) || [0, 0, 0]) as number[];
    const bodyQuat = (robotActions.getBodyRotation(uuid as BodyUUID) || [1, 0, 0, 0]) as number[];

    snapshots.push({
      bodyUuid: uuid,
      startLocalPos: [Number(bodyPos[0] ?? 0), Number(bodyPos[1] ?? 0), Number(bodyPos[2] ?? 0)],
      startQuat: [
        Number(bodyQuat[0] ?? 1),
        Number(bodyQuat[1] ?? 0),
        Number(bodyQuat[2] ?? 0),
        Number(bodyQuat[3] ?? 0),
      ],
      bodyWorldPos,
      parentWorldQuat,
      parentWorldOrigin,
    });
  }
  return snapshots;
}

/** Compute the world-space centroid (average position) of all body snapshots. */
export function computeSnapshotCentroid(snapshots: MultiBodySnapshot[]): THREE.Vector3 {
  const centroid = new THREE.Vector3();
  for (const s of snapshots) centroid.add(s.bodyWorldPos);
  if (snapshots.length > 0) centroid.divideScalar(snapshots.length);
  return centroid;
}
